import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./WoodSupplierTable.css";
import ConflictModal from "./ApproveAssignModal";
import {
  Checkbox,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import viewEye from "assets/viewEye.svg";

// Create a custom theme for the checkboxes
const checkboxTheme = createTheme({
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#757575', // Default unchecked color
          '&.Mui-checked': {
            color: '#102A63', // Custom checked color
          },
        },
      },
    },
  },
});

const RowData = ({ value }) => (
  <td>
    <Tooltip title={value || ""} arrow placement="top">
      <span className="table-cell-content">{value || ""}</span>
    </Tooltip>
  </td>
);

const WoodSupplierTable = ({
  data,
  setData,
  onAssignmentUpdate,
  cropYear,
  cropYearId, // Add these new props
  cropSeasonId,
  onDirectUnassign, // Add this callback
  onViewSurvey // Add this prop
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictMessage, setConflictMessage] = useState("");
  const [unassignWarningOpen, setUnassignWarningOpen] = useState(false);
  const [unassignConfirmOpen, setUnassignConfirmOpen] = useState(false);
  const [itemToUnassign, setItemToUnassign] = useState(null);
  // Add a new state variable to track items checked by "Select All"
  const [itemsCheckedBySelectAll, setItemsCheckedBySelectAll] = useState([]);
  // Get the current logged in user
  const user = JSON.parse(sessionStorage.getItem("BAT_USER"));

  // Check if the current user is a Wood Monitor
  const userProfiles = user?.profiles || [];
  const isWoodMonitor = userProfiles.some(profile =>
    profile.profileName?.toLowerCase().includes("wood monitor")


  );

  const handleSelectAll = () => {
    // Toggle the selectAll state
    const newSelectAll = !selectAll;

    if (newSelectAll) {
      // When CHECKING the "Select All" checkbox:
      // - Keep track of which items are newly checked by this action
      const newlyCheckedIndices = [];

      setData(
        data.map((item, index) => {
          // If the item is already checked, don't change it
          if (item.checked) {
            return item;
          }

          // Check if this item can be selected by the current user
          const canSelect = isWoodMonitor
            ? (!item.assignedTo)
            : true; // Non-Wood Monitor users can select any item

          // If this item is being newly checked, track its index
          if (canSelect) {
            newlyCheckedIndices.push(index);
          }

          return {
            ...item,
            checked: canSelect ? true : item.checked
          };
        })
      );

      // Save the indices of newly checked items
      setItemsCheckedBySelectAll(newlyCheckedIndices);
    } else {
      // When UNCHECKING the "Select All" checkbox:
      // - ONLY uncheck items that were checked by the previous "Select All" action
      // - Keep all other checked items as they are
      setData(
        data.map((item, index) => {
          // If this item was checked by "Select All", uncheck it
          if (itemsCheckedBySelectAll.includes(index)) {
            return {
              ...item,
              checked: false
            };
          }

          // Otherwise keep its current state
          return item;
        })
      );

      // Clear the tracking array since we've unchecked those items
      setItemsCheckedBySelectAll([]);
    }

    setSelectAll(newSelectAll);
  };

  const handleCheckboxChange = async (e, rowIndex) => {
    const { checked } = e.target;
    const itemToUpdate = data[rowIndex];

    // If user is a Wood Monitor and trying to uncheck an item assigned to someone else
    if (isWoodMonitor && !checked && itemToUpdate.assignedToBatOneId != user?.batOneId && itemToUpdate.assignedToBatOneId != null) {
      // Show warning dialog
      setUnassignWarningOpen(true);
      return;
    }


    // Check if this is an unassignment action (unchecking a previously assigned item)
    const isUnassignAction = !checked
    if (isUnassignAction) {
      if (isWoodMonitor && itemToUpdate.assignedToBatOneId != null) {
        handleunassignmentAPI(itemToUpdate)
      } else if (!isWoodMonitor && itemToUpdate.assignedToBatOneId != null) {
        setUnassignConfirmOpen(true);
        setItemToUnassign(itemToUpdate);
      } else {
        setData(
          data.map((ele, index) =>
            index === rowIndex
              ? {
                ...ele,
                checked: false, // We're unchecking the item
                assignedTo: null, // Clear assignment info
                assignedToBatOneId: null
              }
              : ele
          )
        );
      }
    } else {
      // For regular checkbox changes (non-unassignment), update state normally
      setData(
        data.map((ele, index) =>
          index === rowIndex
            ? {
              ...ele,
              checked,
            }
            : ele
        )
      );
    }
  };
  // Update the handleunassignmentAPI function to receive the row index parameter and specify checked value
  const handleunassignmentAPI = async (itemToUpdate) => {
    try {
      setLoading(true);

      // Find the item's index in the data array
      const rowIndex = data.findIndex(item =>
        item.woodSupplierId === itemToUpdate.woodSupplierId ||
        item.headerId === itemToUpdate.headerId
      );

      // Create the payload for the direct unassignment
      const woodSuppliersDetails = [{
        woodSupplierId: itemToUpdate.woodSupplierId || itemToUpdate.headerId,
        isAssigned: false
      }];

      const payload = {
        cropYearId: cropYearId,
        cropSeasonId: cropSeasonId,
        assignedToUserBatOneId: itemToUpdate.assignedToBatOneId,
        woodSuppliersDetails: woodSuppliersDetails
      };

      // Call the parent component to handle the API call
      const success = await onDirectUnassign(payload);

      if (success && rowIndex !== -1) {
        // Update local state only if API call succeeded and we found the item
        setData(
          data.map((ele, index) =>
            index === rowIndex
              ? {
                ...ele,
                checked: false, // We're unchecking the item
                assignedTo: null, // Clear assignment info
                assignedToBatOneId: null
              }
              : ele
          )
        );
      }
    } catch (error) {
      console.error("Error during direct unassignment:", error);
      // Don't update state if there's an error
    } finally {
      setLoading(false);
    }
  }

  const handleUnassignConfirmation = async () => {
    setUnassignConfirmOpen(false);
    if (itemToUnassign) {
      await handleunassignmentAPI(itemToUnassign);
      setItemToUnassign(null);
    }
  };

  // Format date properly for display with time
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    try {
      // Parse the date (handles both Date objects and string dates)
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

      // Check if date is valid
      if (isNaN(date.getTime())) return "";

      // Format the date as DD-MMM-YYYY HH:MM
      const day = date.getDate().toString().padStart(2, '0');

      // Get month in short form (Jan, Feb, etc.)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];

      const year = date.getFullYear();

      // Get hours and minutes with leading zeros
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  return (
    <ThemeProvider theme={checkboxTheme}>
      <Box>
        <div className="ws-container">
          <table className="custom-table-ws">
            <thead>
              <tr>
                <th className="checkbox-column" style={{ width: "50px" }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    sx={{ padding: '4px' }}
                  />
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.lastDateofWS")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsTemporaryCode")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsPermanentCode")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsName")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsState")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsStatus")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.wsSurveyStatus")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.dateOfWSSurvey")}</span>
                </th>
                <th>
                  <span className="header-text">{t("woodSupplierTask.woodMonitor")}</span>
                </th>
                <th className="action-column">
                  <span className="header-text">{t("woodSupplierTask.viewSurvey")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => (
                <tr key={index}>
                  <td className="checkbox-column">
                    {/* Remove tooltip wrapper and just keep the checkbox */}
                    <Checkbox
                      checked={item?.checked || false}
                      onChange={(e) => handleCheckboxChange(e, index)}
                      disabled={item?.status == 'Inactive'}
                      sx={{ padding: '4px' }}
                    />
                  </td>
                  <td>{formatDate(item?.ftSurveyDate)}</td>
                  <td className="ws-temp-code">
                      <span className="table-cell-content">{item?.wsTemporaryCode || ""}</span>
                  </td>
                  <td className="ws-temp-code">
                      <span className="table-cell-content">{item?.wsPermanentCode || ""}</span>
                  </td>
                  <td>{item?.woodSupplierName}</td>
                  <td>{item?.location}</td>
                  <td>{item?.status}</td>
                  <td>{item?.wsSurveyStatus}</td>
                  <td>{formatDate(item?.wsSurveyDate)}</td>
                  <td>{item?.assignedTo}</td>
                  <td style={{justifyContent:'center'}} onClick={() => onViewSurvey(item)}>
                    <Box>
                      <img style={{ cursor: 'pointer' }} src={viewEye} alt="View Survey"/>
                    </Box>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 2
              }}
            >
              <CircularProgress />
            </div>
          )}
        </div>

        {/* Conflict Modal */}
        <ConflictModal
          open={conflictModalOpen}
          onClose={() => setConflictModalOpen(false)}
          message={conflictMessage}
        />

        {/* Warning Dialog for Wood Monitor unassignment - styled like ApproveModal */}
        <Dialog
          open={unassignWarningOpen}
          onClose={() => setUnassignWarningOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '8px',
              padding: 0,
              minHeight: 'fit-content',
              maxHeight: 'fit-content'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0 16px',
              backgroundColor: '#102A62',
              margin: 0,
              width: '100%',
              height: '50px',
              boxSizing: 'border-box',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                color: '#FFFFFF',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                textAlign: 'center'
              }}
            >
              Confirmation
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: '20px',
              overflow: 'hidden',
              marginTop: '20px',
            }}
          >
            <Typography
              sx={{
                color: '#000000DE',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                lineHeight: '24px',
                marginBottom: '20px'
              }}
            >
              You can only unassign Wood Suppliers who are currently assigned to you.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '0 20px 20px 20px',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={() => setUnassignWarningOpen(false)}
              variant="contained"
              sx={{
                backgroundColor: '#004F9F',
                color: 'white',
                textTransform: 'none',
                fontFamily: 'Montserrat',
                '&:hover': {
                  backgroundColor: '#004F9F'
                },
                minWidth: '100px'
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {/* Unassign Confirmation Dialog */}
        <Dialog
          open={unassignConfirmOpen}
          onClose={() => setUnassignConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '8px',
              padding: 0,
              minHeight: 'fit-content',
              maxHeight: 'fit-content'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0 16px',
              backgroundColor: '#102A62',
              margin: 0,
              width: '100%',
              height: '50px',
              boxSizing: 'border-box',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                color: '#FFFFFF',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                textAlign: 'center'
              }}
            >
              Confirmation
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: '20px',
              overflow: 'hidden',
              marginTop: '20px',
            }}
          >
            <Typography
              sx={{
                color: '#000000DE',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                lineHeight: '24px',
                marginBottom: '20px'
              }}
            >
              You are attempting to unselect Wood Suppliers assigned to someone else. Are you sure you want to proceed?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '0 20px 20px 20px',
              justifyContent: 'center',
              gap: 6
            }}
          >
            <Button
              onClick={() => setUnassignConfirmOpen(false)}
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: '#62636C',
                background: '#62636C',
                textTransform: 'none',
                fontFamily: 'Montserrat',
                '&:hover': {
                  color: 'white',
                  background: '#62636C',
                }
              }}
            >
              No
            </Button>
            <Button
              onClick={() => handleUnassignConfirmation()}
              variant="contained"
              sx={{
                backgroundColor: '#004F9F',
                color: 'white',
                textTransform: 'none',
                fontFamily: 'Montserrat',
                '&:hover': {
                  backgroundColor: '#004F9F'
                }
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default WoodSupplierTable;
